import { useCallback, useMemo, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useSendTransaction } from '@privy-io/react-auth/solana';
import { 
  Connection, 
  PublicKey,
  SystemProgram,
  ComputeBudgetProgram,
  LAMPORTS_PER_SOL,
} from '@solana/web3.js';
import { Program, AnchorProvider, BN } from '@coral-xyz/anchor';
import { solanaUtils } from '../utils/solanaUtils';
import { waitForTransactionConfirmation } from '../utils/transactionParser';
import OpinionMarketIDL from '../idl/opinion_market.json';

const OPINION_MARKET_PROGRAM_ID = new PublicKey(OpinionMarketIDL.address);
const DEFAULT_FEE_RECIPIENT = new PublicKey('42kX3VMXNNF8eadz9RtpQbseKCGYcwK5Z8iEsxxSRtzg');

export interface MarketTransactionResult {
  success: boolean;
  signature?: string;
  error?: string;
}

export interface CreateMarketResult extends MarketTransactionResult {
  marketAddress?: string;
}

export interface CreateMarketParams {
  question: string;
  optionNames: string[];
  startTime: Date;
  endTime: Date;
}

/**
 * Hook for Opinion Market transactions (buy_tokens, sell_tokens)
 * 
 * Note: change_option was removed in the new IDL. Users must sell first, then buy new option.
 */
export const useMarketTransactions = (solanaWallet: any) => {
  const { authenticated } = usePrivy();
  const { sendTransaction } = useSendTransaction();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check wallet type
  const isEmbeddedWallet = solanaWallet?.walletClientType === 'privy';
  const isExternalWallet = solanaWallet?.walletClientType === 'phantom' || 
                           solanaWallet?.connectorType === 'solana_adapter';
  
  const connection = useMemo(
    () => new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'), 
    []
  );

  /**
   * Get Opinion Market PDAs
   * 
   * New IDL uses market address directly (PDA derived from creator + nonce on-chain)
   * Position PDA: seeds = ["position", market_pubkey, user_pubkey]
   */
  const getPDAs = useCallback((marketAddress: string, userWallet: PublicKey) => {
    const marketPDA = new PublicKey(marketAddress);
    
    // Protocol PDA: seeds = ["protocol"]
    const [protocolPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("protocol")],
      OPINION_MARKET_PROGRAM_ID
    );

    // Position PDA: seeds = ["position", market_pubkey, user_pubkey]
    const [positionPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("position"), marketPDA.toBuffer(), userWallet.toBuffer()],
      OPINION_MARKET_PROGRAM_ID
    );

    return { protocolPDA, marketPDA, positionPDA };
  }, []);

  /**
   * Buy tokens for a market option
   * 
   * User selects an option and deposits SOL to buy tokens. Fee is taken and sent to fee_recipient.
   * Tokens are calculated based on bonding curve (early depositors get more tokens).
   * One position per user per market - cannot change option after buying (must sell first).
   * 
   * @param marketAddress - Market PDA address (on-chain)
   * @param optionIndex - Index of option to bet on (0, 1, 2, etc.)
   * @param amountSol - Amount in SOL to deposit
   * @param marketCreator - Market creator's wallet address (for creator fee)
   * @param feeRecipient - Fee recipient address (from protocol config or backend)
   * @returns Transaction signature
   */
  const buyTokens = useCallback(async (
    marketAddress: string,
    optionIndex: number,
    amountSol: number,
    marketCreator: string,
    feeRecipient?: string
  ): Promise<MarketTransactionResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const userWallet = new PublicKey(solanaWallet.address);
      const feeRecipientPubkey = feeRecipient 
        ? new PublicKey(feeRecipient) 
        : DEFAULT_FEE_RECIPIENT;
      const marketCreatorPubkey = new PublicKey(marketCreator);
      
      const { protocolPDA, marketPDA, positionPDA } = getPDAs(marketAddress, userWallet);

      const amountLamports = Math.floor(amountSol * LAMPORTS_PER_SOL);

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: userWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(OpinionMarketIDL as any, provider);

      // Build buy_tokens transaction
      const transaction = await program.methods
        .buyTokens(
          optionIndex,
          new BN(amountLamports)
        )
        .accounts({
          protocol: protocolPDA,
          market: marketPDA,
          position: positionPDA,
          feeRecipient: feeRecipientPubkey,
          marketCreator: marketCreatorPubkey,
          user: userWallet,
          systemProgram: SystemProgram.programId,
        })
        .preInstructions([
          // Compute budget for priority
          ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userWallet;

      // Simulate transaction before signing
      try {
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      setIsLoading(false);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Buy tokens failed:', error);
      const errMsg = error.message || 'Failed to buy tokens';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet,
    getPDAs
  ]);

  /**
   * Sell tokens from an option
   * 
   * User sells their tokens back to the pool and receives SOL.
   * Option is determined from user's position (no need to pass option_index).
   * Before end_time: Can sell any option.
   * After end_time: Can only sell winning option (others are delisted).
   * 
   * @param marketAddress - Market PDA address (on-chain)
   * @param amountTokens - Amount of tokens to sell (in lamports/base units)
   * @param marketCreator - Market creator's wallet address (for creator fee)
   * @param feeRecipient - Fee recipient address (from protocol config or backend)
   * @returns Transaction signature
   */
  const sellTokens = useCallback(async (
    marketAddress: string,
    amountTokens: number,
    marketCreator: string,
    feeRecipient?: string
  ): Promise<MarketTransactionResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const userWallet = new PublicKey(solanaWallet.address);
      const feeRecipientPubkey = feeRecipient 
        ? new PublicKey(feeRecipient) 
        : DEFAULT_FEE_RECIPIENT;
      const marketCreatorPubkey = new PublicKey(marketCreator);
      
      const { protocolPDA, marketPDA, positionPDA } = getPDAs(marketAddress, userWallet);

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: userWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(OpinionMarketIDL as any, provider);

      // Build sell_tokens transaction
      const transaction = await program.methods
        .sellTokens(new BN(amountTokens))
        .accounts({
          protocol: protocolPDA,
          market: marketPDA,
          position: positionPDA,
          feeRecipient: feeRecipientPubkey,
          marketCreator: marketCreatorPubkey,
          user: userWallet,
        })
        .preInstructions([
          // Compute budget for priority
          ComputeBudgetProgram.setComputeUnitLimit({ units: 200_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = userWallet;

      // Simulate transaction before signing
      try {
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      setIsLoading(false);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Sell tokens failed:', error);
      const errMsg = error.message || 'Failed to sell tokens';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet,
    getPDAs
  ]);

  /**
   * Create a new market (permissionless)
   * 
   * Anyone can create a market with specified options and time window.
   * Market nonce is auto-assigned from creator's market counter.
   * Creator earns a share of trading fees based on protocol settings.
   * 
   * @param params - Market creation parameters
   * @returns Transaction signature and market address
   */
  const createMarket = useCallback(async (
    params: CreateMarketParams
  ): Promise<CreateMarketResult> => {
    setIsLoading(true);
    setError(null);

    if (!authenticated || !solanaWallet?.address) {
      const err = 'Wallet not connected';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    const { question, optionNames, startTime, endTime } = params;

    // Validate inputs
    if (!question || question.trim().length === 0) {
      const err = 'Question is required';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    if (!optionNames || optionNames.length < 2) {
      const err = 'At least 2 options are required';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    if (endTime <= startTime) {
      const err = 'End time must be after start time';
      setError(err);
      setIsLoading(false);
      return { success: false, error: err };
    }

    try {
      const creatorWallet = new PublicKey(solanaWallet.address);

      // Protocol PDA: seeds = ["protocol"]
      const [protocolPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from("protocol")],
        OPINION_MARKET_PROGRAM_ID
      );

      // Creator State PDA: seeds = ["creator", creator_pubkey]
      const [creatorStatePDA] = PublicKey.findProgramAddressSync(
        [Buffer.from("creator"), creatorWallet.toBuffer()],
        OPINION_MARKET_PROGRAM_ID
      );

      // Create program instance
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface
        {
          publicKey: creatorWallet,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(OpinionMarketIDL as any, provider);

      // Fetch creator state to get the next market nonce
      // If creator state doesn't exist, it will be created and nonce will be 0
      let marketNonce = 0;
      try {
        // Use any cast to access dynamically typed account namespace
        const creatorState = await (program.account as any).creatorState.fetch(creatorStatePDA);
        marketNonce = creatorState.marketsCreated;
      } catch {
        // Creator state doesn't exist yet, nonce will be 0
        marketNonce = 0;
      }

      // Market PDA: seeds = ["market", creator_pubkey, market_nonce]
      const marketNonceBuffer = Buffer.alloc(8);
      marketNonceBuffer.writeBigUInt64LE(BigInt(marketNonce));
      
      const [marketPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from("market"), creatorWallet.toBuffer(), marketNonceBuffer],
        OPINION_MARKET_PROGRAM_ID
      );

      // Convert timestamps to Unix seconds (i64)
      const startTimestamp = new BN(Math.floor(startTime.getTime() / 1000));
      const endTimestamp = new BN(Math.floor(endTime.getTime() / 1000));

      // Build create_market transaction
      const transaction = await program.methods
        .createMarket(
          question,
          optionNames,
          startTimestamp,
          endTimestamp
        )
        .accounts({
          protocol: protocolPDA,
          creatorState: creatorStatePDA,
          market: marketPDA,
          creator: creatorWallet,
          systemProgram: SystemProgram.programId,
        })
        .preInstructions([
          // Compute budget for priority
          ComputeBudgetProgram.setComputeUnitLimit({ units: 300_000 }),
          ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 375_000 }),
        ])
        .transaction();

      // Set recent blockhash and fee payer
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = creatorWallet;

      // Simulate transaction before signing
      try {
        transaction.signatures = transaction.signatures.map(sig => ({
          ...sig,
          signature: null
        }));
        
        const simulation = await connection.simulateTransaction(transaction);
        
        if (simulation.value.err) {
          console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
          throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
        }
      } catch (simError: any) {
        throw new Error(`Pre-flight simulation failed: ${simError.message}`);
      }

      // Sign and send transaction
      let signature: string;

      if (isExternalWallet && solanaWallet?.signTransaction) {
        try {
          const signedTx = await solanaWallet.signTransaction(transaction);
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: true,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } catch (walletError: any) {
          console.error('Wallet signing failed:', walletError);
          throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
        }
      } else if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: true },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }

      // Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction failed to confirm');
      }

      console.log('✅ Market created:', marketPDA.toBase58());

      setIsLoading(false);
      return {
        success: true,
        signature,
        marketAddress: marketPDA.toBase58()
      };

    } catch (error: any) {
      console.error('❌ Create market failed:', error);
      const errMsg = error.message || 'Failed to create market';
      setError(errMsg);
      setIsLoading(false);
      return {
        success: false,
        error: errMsg
      };
    }
  }, [
    authenticated,
    solanaWallet,
    connection,
    sendTransaction,
    isEmbeddedWallet,
    isExternalWallet
  ]);

  return {
    // Core functions
    buyTokens,
    sellTokens,
    createMarket,
    
    // Transaction state
    isLoading,
    error,
    
    // Wallet info
    walletAddress: solanaWallet?.address,
    isConnected: authenticated && !!solanaWallet?.address,
    
    // Config
    config: {
      rpcEndpoint: solanaUtils.getRpcEndpoint(),
      commitment: 'confirmed' as const,
      programId: OPINION_MARKET_PROGRAM_ID.toBase58(),
    }
  };
};
